import {
	ContactListItem,
	ContactPresenceStatus,
	ContactSource,
	getWorkspaceContactsPresence,
	listWorkspaceContacts,
	WorkspaceContactSortBy,
	WorkspaceContactsPresence
} from "client/Api/Email/contacts";
import {
	AlertManager,
	useAlerts,
	createRemoteDataSource,
	LazyLoadCursorKind
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { Poller } from "client_legacy/ndaw/poller";
import { addressBookLiveTilePageSize } from "../AddressBookLiveTileContent";
import { ContactPresence, GenericContactsTab } from "./GenericContactsTab";

export type WorkspaceContactsTabProps = {
	department?: string | null;
	presenceUpdateInterval: number;
	searchTerm: VersionedValue<string>;
	onSearchTermChange(newValue: string, version: Guid): void;
	onListItemClick(): void;
};

export const WorkspaceContactsTab = (props: WorkspaceContactsTabProps): ReactElement => {
	const {
		department,
		presenceUpdateInterval,
		searchTerm,
		onSearchTermChange,
		onListItemClick
	} = props;

	const didShowAlertThatLiveUpdatesAreStoppedRef = useRef<boolean>(false);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(addressBookLiveTilePageSize);
	const [contactPresenceStatuses, setContactPresenceStatuses] = useState<WorkspaceContactsPresence>({});
	const alertManager: AlertManager = useAlerts();

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		reload
	} = useWorkspaceContactsDataSource({
		filtering: {
			department: department ?? null
		},
		searchTerm: searchTerm.value,
		desiredAmountOfItems
	});

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm, department);
			setDesiredAmountOfItems(addressBookLiveTilePageSize);
		},
		[searchTerm, department]
	);

	const isMoreItemsThenPossibleToPollLiveStatuses: boolean = currentItems.length
		> addressBookLiveTilePageSize;

	useEffect(
		() => {
			if (isMoreItemsThenPossibleToPollLiveStatuses) {
				if (!didShowAlertThatLiveUpdatesAreStoppedRef.current) {
					alertManager.showInfo(localize("_UserPresenceIsNoLongerRetrievedInTheAddressBookLiveTile"));
					didShowAlertThatLiveUpdatesAreStoppedRef.current = true;
				}
				return undefined;
			}
			didShowAlertThatLiveUpdatesAreStoppedRef.current = false;

			const poller = new Poller({
				intervalInSeconds: presenceUpdateInterval,
				shouldInvokeImmediately: false,
				callback: async (signal): Promise<void> => {
					const statuses: WorkspaceContactsPresence = await getWorkspaceContactsPresence(
						currentItems.map(i => i.id),
						signal
					);

					setContactPresenceStatuses(statuses);
				}
			});
			poller.start();

			return () => {
				poller.destroy();
			};
		},
		[
			alertManager,
			currentItems,
			presenceUpdateInterval,
			isMoreItemsThenPossibleToPollLiveStatuses
		]
	);

	const presenceStatuses = useMemo(
		(): ContactPresence[] | undefined => {
			if (isMoreItemsThenPossibleToPollLiveStatuses) {
				return undefined;
			}

			return currentItems.map(
				(contact: ContactListItem): ContactPresence => {
					const {
						id,
						presenceStatus: initialStatus
					} = contact;

					const presenceStatus: ContactPresenceStatus | undefined = contactPresenceStatuses[contact.id];

					return {
						contactId: id,
						status: typeof presenceStatus === "undefined"
							? initialStatus
							: presenceStatus
					};
				}
			);
		},
		[
			contactPresenceStatuses,
			currentItems,
			isMoreItemsThenPossibleToPollLiveStatuses
		]
	);

	return (
		<GenericContactsTab
			contacts={currentItems}
			loadingState={getLoadingState()}
			searchTerm={searchTerm}
			onSearchTermChange={onSearchTermChange}
			source={ContactSource.Workspace}
			hasMore={hasMoreOnServer}
			onLoadMore={() => setDesiredAmountOfItems(a => a + addressBookLiveTilePageSize)}
			onRetry={reload}
			presenceStatuses={presenceStatuses}
			onListItemClick={onListItemClick}
		/>
	);
};
WorkspaceContactsTab.displayName = "WorkspaceContactsTab";

type ContactsFilter = {
	department: string | null;
};

const useWorkspaceContactsDataSource = createRemoteDataSource<ContactListItem, ContactsFilter>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake,
		hasFiltering: true
	})
	.getHook({
		performRequest: async (options) => {
			return await listWorkspaceContacts(
				options.searchTerm,
				null,
				options.filterState
					.department,
				options.amount,
				options.skip,
				WorkspaceContactSortBy.Default,
				options.signal
			);
		}
	});